// Cart drawer upsale list.
//
// The drawer re-renders its whole inner HTML on every cart change, which means this
// element is thrown away and rebuilt each time. Products already in the cart are filtered
// out server side, so the list is always correct after a render - the only thing missing is
// the movement. We keep the ids of the previously rendered cards in module scope and fade
// in whatever is new, which is how a product removed from the cart slides back into place.

const CART_UPSELL_LEAVE_DURATION = 320;

let cartUpsellRenderedIds = null;

class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.list = this.querySelector('.cart-upsell__list');
    if (!this.list || this.initialized) return;
    this.initialized = true;

    this.slides = Array.from(this.list.querySelectorAll('.cart-upsell__slide'));
    this.errorElement = this.querySelector('.cart-upsell__error');

    this.animateNewSlides();
    this.addEventListener('click', this.onClick);
    this.watchDrawer();

    if (this.isDrawerOpen()) this.initSlider();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.drawerObserver) this.drawerObserver.disconnect();
    this.destroySlider();
  }

  // Cards that were not in the previous render are new to the customer - either the cart
  // shrank or the drawer was reopened after a change, so they get faded in.
  animateNewSlides() {
    const currentIds = this.slides.map((slide) => slide.dataset.upsellId);

    if (cartUpsellRenderedIds) {
      const fresh = this.slides.filter((slide) => !cartUpsellRenderedIds.has(slide.dataset.upsellId));
      if (fresh.length) {
        fresh.forEach((slide) => slide.classList.add('cart-upsell__slide--enter'));
        // Two frames: the first commits the "from" state, the second starts the transition
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            fresh.forEach((slide) => slide.classList.remove('cart-upsell__slide--enter'));
          });
        });
      }
    }

    cartUpsellRenderedIds = new Set(currentIds);
  }

  onClick(event) {
    const button = event.target.closest('.cart-upsell__add');
    // Multi variant products link to the product page instead of adding straight away
    if (!button || button.tagName === 'A' || button.disabled) return;
    event.preventDefault();
    this.addToCart(button);
  }

  addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const slide = button.closest('.cart-upsell__slide');
    this.hideError();
    button.disabled = true;
    button.classList.add('loading');
    const spinner = button.querySelector('.cart-upsell__add-spinner');
    if (spinner) spinner.classList.remove('hidden');

    // The card animates out while the request is in flight, and the drawer is only
    // re-rendered once both have finished so the two never fight each other.
    Promise.all([this.postToCart(variantId), this.animateOut(slide)])
      .then(([response]) => {
        if (response.status) {
          this.restoreSlide(slide, button);
          this.showError(response.description || response.message);
          return undefined;
        }
        return Promise.resolve(this.renderCart(response.sections)).then(() => this.syncFreeGift());
      })
      .catch((error) => {
        console.error('Could not add the upsale product to the cart:', error.message);
        this.restoreSlide(slide, button);
        this.showError();
      });
  }

  postToCart(variantId) {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('sections', 'cart-drawer,cart-icon-bubble');
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
  }

  // The theme re-checks the gift threshold on every other add to cart path. An upsale can
  // only push the cart total up, so the gift may need to be added, never removed.
  syncFreeGift() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return undefined;

    const threshold = Number(drawer.getAttribute('data-cart-giftThreshold'));
    const giftVariantId = drawer.getAttribute('data-cart-gift');
    if (!threshold || !giftVariantId) return undefined;

    return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const hasGift = (cart.items || []).some(
          (item) => String(item.variant_id) === String(giftVariantId)
        );
        if (hasGift || cart.items_subtotal_price / 100 <= threshold) return undefined;

        return this.postToCart(giftVariantId).then((response) => {
          if (response.status) return undefined;
          return this.renderCart(response.sections);
        });
      })
      .catch((error) => {
        console.warn('Could not check the cart gift threshold:', error.message);
      });
  }

  animateOut(slide) {
    if (!slide) return Promise.resolve();
    slide.classList.add('cart-upsell__slide--leaving');
    // Matches the transition in component-cart-upsell.css
    return new Promise((resolve) => setTimeout(resolve, CART_UPSELL_LEAVE_DURATION));
  }

  restoreSlide(slide, button) {
    if (slide) slide.classList.remove('cart-upsell__slide--leaving');
    if (button) {
      button.disabled = false;
      button.classList.remove('loading');
      const spinner = button.querySelector('.cart-upsell__add-spinner');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message || window.cartStrings.error;
    this.errorElement.classList.remove('hidden');
  }

  hideError() {
    if (!this.errorElement) return;
    this.errorElement.classList.add('hidden');
  }

  renderCart(sections) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return undefined;

    if (sections && sections['cart-drawer']) {
      this.applySections(drawer, sections);
      return undefined;
    }

    return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => response.json())
      .then((fetched) => this.applySections(drawer, fetched));
  }

  // Swaps the drawer sections in place. renderContents() would work too, but it calls
  // open() which fetches the same sections a second time and restarts the open animation.
  applySections(drawer, sections) {
    const promoWasOpen = document.querySelector('.promo-code-wrap.open') !== null;
    const container = document.querySelector('#CartDrawer');
    if (!container) return;

    drawer.classList.remove('is-empty');
    const inner = drawer.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');

    container.innerHTML = drawer.getSectionInnerHTML(sections['cart-drawer'], '#CartDrawer');

    const iconBubble = document.getElementById('cart-icon-bubble');
    if (iconBubble && sections['cart-icon-bubble']) {
      iconBubble.innerHTML = drawer.getSectionInnerHTML(sections['cart-icon-bubble']);
    }

    // The overlay is part of the replaced markup, so its close handler has to be re-bound
    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));

    if (promoWasOpen) {
      const promoWrap = drawer.querySelector('.promo-code-wrap');
      if (promoWrap) promoWrap.classList.add('open');
    }
  }

  isDrawerOpen() {
    const drawer = this.closest('cart-drawer');
    return Boolean(drawer && drawer.classList.contains('active'));
  }

  // Flickity cannot measure the cells while the drawer is still translated off screen,
  // so the slider waits for the drawer to open.
  watchDrawer() {
    const drawer = this.closest('cart-drawer');
    if (!drawer || this.dataset.slider !== 'true') return;

    this.drawerObserver = new MutationObserver(() => {
      if (!drawer.classList.contains('active')) return;
      if (this.flkty) {
        this.flkty.resize();
      } else {
        this.initSlider();
      }
    });
    this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  initSlider() {
    if (this.flkty || this.dataset.slider !== 'true') return;
    if (typeof Flickity === 'undefined' || this.slides.length < 2) return;
    if (!this.list.offsetWidth) return;

    const isRtl = this.dataset.rtl === 'true';

    this.flkty = new Flickity(this.list, {
      rightToLeft: isRtl,
      cellAlign: isRtl ? 'right' : 'left',
      contain: true,
      wrapAround: false,
      imagesLoaded: true,
      pageDots: false,
      prevNextButtons: false,
      // The drawer traps focus, arrow keys should not move the carousel instead
      accessibility: false,
    });
  }

  destroySlider() {
    if (!this.flkty) return;
    try {
      this.flkty.destroy();
    } catch (error) {
      // The element is already detached, nothing left to clean up
    }
    this.flkty = null;
  }
}

customElements.define('cart-upsell', CartUpsell);
